import { useNavigate, useParams } from "@tanstack/react-router"
import { map } from "lodash-es"
import { useEffect, useState } from "react"
import { useHotkeys } from "react-hotkeys-hook"

import { trpc } from "../lib/trpc"

interface BookPage {
  pageNumber: number
  text: string
  imagePrompt: string
  imageUrl?: string
  imageStatus?: "pending" | "generating" | "completed" | "failed"
}

interface BookContent {
  title: string
  description: string
  pages: BookPage[]
  totalPages: number
  ageRange: string
  themes: string[]
  status: string
  artStyle?: {
    key: string
    name: string
    description: string
  }
}

/**
 * Book Reader Screen - Interactive Children's Book Viewer
 *
 * Features:
 * - Page-by-page book reading experience
 * - Full-screen image viewing
 * - Navigation controls (previous/next page)
 * - Progress indicator
 * - Responsive design for different screen sizes
 * - Back to library navigation
 */
function BookReaderScreen() {
  const { bookId } = useParams({ from: "/book/$bookId" })
  const navigate = useNavigate()

  const [currentPage, setCurrentPage] = useState(0)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null)

  // tRPC hook to fetch book data
  const {
    data: bookData,
    isLoading,
    error,
  } = trpc.childrenBooks.getBook.useQuery({
    id: bookId,
  })

  const book = bookData?.success ? bookData.book : null
  const bookContent: BookContent | null = book?.bookData ? book.bookData : null

  // Handle keyboard navigation using react-hotkeys-hook

  // Escape key handling (context-dependent)
  useHotkeys("escape", () => {
    if (isFullscreen) {
      setIsFullscreen(false)
      setSelectedImageUrl(null)
    } else {
      navigate({ to: "/children-books" })
    }
  }, [isFullscreen, navigate])

  // Arrow key navigation (only when not in fullscreen and bookContent exists)
  useHotkeys("left", () => {
    if (!isFullscreen && bookContent && currentPage > 0) {
      setCurrentPage(currentPage - 1)
    }
  }, [currentPage, bookContent, isFullscreen])

  useHotkeys("right", () => {
    if (!isFullscreen && bookContent && currentPage < bookContent.pages.length - 1) {
      setCurrentPage(currentPage + 1)
    }
  }, [currentPage, bookContent, isFullscreen])

  // Home and End keys
  useHotkeys("home", () => {
    if (!isFullscreen && bookContent) {
      setCurrentPage(0)
    }
  }, [bookContent, isFullscreen])

  useHotkeys("end", () => {
    if (!isFullscreen && bookContent) {
      setCurrentPage(bookContent.pages.length - 1)
    }
  }, [bookContent, isFullscreen])

  const handleImageClick = (imageUrl: string) => {
    setSelectedImageUrl(imageUrl)
    setIsFullscreen(true)
  }

  const goToNextPage = () => {
    if (bookContent && currentPage < bookContent.pages.length - 1) {
      setCurrentPage(currentPage + 1)
    }
  }

  const goToPreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1)
    }
  }

  const goToPage = (pageIndex: number) => {
    if (bookContent && pageIndex >= 0 && pageIndex < bookContent.pages.length) {
      setCurrentPage(pageIndex)
    }
  }

  if (isLoading) {
    return (
      <div className="p-8 max-w-7xl mx-auto">
        <div className="text-center py-12">
          <div className="text-gray-500 text-lg">Loading book...</div>
          <div className="mt-2 text-sm text-gray-400">Please wait while we fetch your story</div>
        </div>
      </div>
    )
  }

  if (error || !book) {
    return (
      <div className="p-8 max-w-7xl mx-auto">
        <div className="text-center py-12">
          <div className="text-destructive text-lg mb-4">❌ Book not found</div>
          <p className=" mb-6">The book you&apos;re looking for doesn&apos;t exist or has been removed.</p>
          <button
            onClick={() => navigate({ to: "/children-books" })}
            className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            ← Back to Library
          </button>
        </div>
      </div>
    )
  }

  if (!bookContent) {
    return (
      <div className="p-8 max-w-7xl mx-auto">
        <div className="text-center py-12">
          <div className="text-yellow-500 text-lg mb-4">⚠️ Book content unavailable</div>
          <p className=" mb-6">This book is still being generated or the content is corrupted.</p>
          <div className="space-x-4">
            <button
              onClick={() => navigate({ to: "/children-books" })}
              className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              ← Back to Library
            </button>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 border border-gray-300 text-primary rounded-md  transition-colors"
            >
              🔄 Refresh
            </button>
          </div>
        </div>
      </div>
    )
  }

  const currentPageData = bookContent.pages[currentPage]
  const progress = ((currentPage + 1) / bookContent.pages.length) * 100

  // Safety check for current page data
  if (!currentPageData) {
    return (
      <div className="p-8 max-w-7xl mx-auto">
        <div className="text-center py-12">
          <div className="text-destructive text-lg mb-4">❌ Page not found</div>
          <p className=" mb-6">The requested page doesn&apos;t exist in this book.</p>
          <button
            onClick={() => navigate({ to: "/children-books" })}
            className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            ← Back to Library
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
      {/* Header */}
      <header className="bg-background shadow-sm border-b border-border px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate({ to: "/children-books" })}
              className="px-4 py-2  hover:text-gray-800  rounded-md transition-colors"
            >
              ← Back to Library
            </button>
            <div>
              <h1 className="text-xl font-bold ">{bookContent.title}</h1>
              <p className="text-sm text-gray-500">
                Page {currentPage + 1} of {bookContent.pages.length}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <div className="text-sm text-gray-500">Age: {bookContent.ageRange}</div>
            <div className="w-32 bg-foregroundx rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
      </header>

      {/* Main Reader */}
      <main className="max-w-6xl mx-auto p-6">
        <div className="bg-background rounded-lg shadow-lg overflow-hidden">
          {/* Page Content */}
          <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[600px]">
            {/* Image Side */}
            <div className="bg-foregroundx flex items-center justify-center p-8">
              {currentPageData.imageUrl ? (
                <div className="w-full h-full max-w-md max-h-96 relative group">
                  <img
                    src={currentPageData.imageUrl}
                    alt={currentPageData.imagePrompt}
                    className="w-full h-full object-contain rounded-lg shadow-md cursor-pointer hover:shadow-lg transition-shadow"
                    onClick={() => handleImageClick(currentPageData.imageUrl)}
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-200 rounded-lg flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-background px-3 py-1 rounded-full text-sm font-medium">
                      Click to enlarge
                    </div>
                  </div>
                </div>
              ) : (
                <div className="w-full h-64 bg-foregroundx rounded-lg flex items-center justify-center">
                  <div className="text-center text-gray-500">
                    <div className="text-4xl mb-2">🎨</div>
                    <div className="text-sm">
                      {currentPageData.imageStatus === "failed" ? "Image failed to generate" : "No image available"}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Text Side */}
            <div className="p-8 flex flex-col justify-center">
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-800 leading-relaxed text-lg">{currentPageData.text}</p>
              </div>

              {/* Page Navigation */}
              <div className="mt-8 flex items-center justify-between">
                <button
                  onClick={goToPreviousPage}
                  disabled={currentPage === 0}
                  className="px-4 py-2 bg-foregroundx text-primary rounded-md  disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  ← Previous
                </button>

                <div className="text-sm text-gray-500 font-medium">Page {currentPage + 1}</div>

                <button
                  onClick={goToNextPage}
                  disabled={currentPage === bookContent.pages.length - 1}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next →
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Page Thumbnails */}
        <div className="mt-6 bg-background rounded-lg shadow-sm p-4">
          <h3 className="text-sm font-medium text-primary mb-3">Jump to page:</h3>
          <div className="flex space-x-2 overflow-x-auto pb-2">
            {map(bookContent.pages, (page, index) => (
              <button
                key={index}
                onClick={() => goToPage(index)}
                className={`flex-shrink-0 w-16 h-20 rounded border-2 transition-all duration-200 ${
                  index === currentPage
                    ? "border-blue-500 bg-blue-50"
                    : "border-border hover:border-gray-300 bg-foregroundx"
                }`}
              >
                <div className="w-full h-12 bg-foregroundx rounded-t flex items-center justify-center text-xs">
                  {page.imageUrl ? "🖼️" : "📄"}
                </div>
                <div className="text-xs text-center py-1 font-medium">{index + 1}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Book Info */}
        {bookContent.themes && (
          <div className="mt-6 bg-background rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold  mb-4">About This Book</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className=" mb-4">{bookContent.description}</p>
                <div className="text-sm text-gray-500">
                  <p>
                    <strong>Age Range:</strong> {bookContent.ageRange}
                  </p>
                  <p>
                    <strong>Total Pages:</strong> {bookContent.pages.length}
                  </p>
                  {bookContent.artStyle && (
                    <p>
                      <strong>Art Style:</strong> {bookContent.artStyle.name}
                    </p>
                  )}
                </div>
                {bookContent.artStyle && (
                  <div className="mt-3 p-3 bg-foregroundx rounded-md">
                    <p className="text-sm ">
                      <strong>Style Description:</strong> {bookContent.artStyle.description}
                    </p>
                  </div>
                )}
              </div>
              <div>
                <p className="text-sm font-medium text-primary mb-2">Themes:</p>
                <div className="flex flex-wrap gap-2">
                  {map(bookContent.themes, (theme, index) => (
                    <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                      {theme}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Fullscreen Image Modal */}
      {isFullscreen && selectedImageUrl && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
          <div className="relative max-w-full max-h-full">
            <img src={selectedImageUrl} alt="Fullscreen view" className="max-w-full max-h-full object-contain" />
            <button
              onClick={() => {
                setIsFullscreen(false)
                setSelectedImageUrl(null)
              }}
              className="absolute top-4 right-4 bg-background text-white p-2 rounded-full transition-colors"
            >
              ✕
            </button>
          </div>
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-sm bg-black/50 px-3 py-1 rounded">
            Press ESC to close
          </div>
        </div>
      )}

      {/* Keyboard Shortcuts Help */}
      <div className="fixed bottom-4 right-4 bg-background shadow-lg rounded-lg p-3 text-xs  max-w-xs">
        <div className="font-medium mb-1">Keyboard Shortcuts:</div>
        <div>← → Navigate pages</div>
        <div>Home/End: First/Last page</div>
        <div>ESC: Back to library</div>
      </div>
    </div>
  )
}

export default BookReaderScreen
